import TitleBar from '../../../components/TitleBar';
import { useEffect } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Footer } from '../../../components/common/Footer';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components';
import { FETCH_CORPORATE_VIEW } from '../queries';
import { useQuery } from '@apollo/client';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import QueryErrorBoundary from '../../../components/error-handling';
import { baseColumnsCorporate } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

type CorporateView = {
  name: string;
  percentage: number;
  supportedRevenue: number;
  uuid: string;
};

interface DataReturned {
  corporateView: CorporateView[];
}

function CorporateView() {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<DataReturned>(FETCH_CORPORATE_VIEW);

  const locationsData = data?.corporateView || [];

  const { selectedTags, isLocation, isDivision } = useUserContext();

  const filteredLocations = getFilteredTags({
    data: locationsData,
    selectedTags,
    condition: isLocation,
    lookUpKey: 'uuid',
  });

  const columns: ColumnsType<CorporateView> = [
    {
      title: 'Location',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      align: 'left',
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span className="flex items-center gap-4">
          Company Wide
          <br />
          <Button
            type={'link'}
            htmlType={'button'}
            onClick={() => navigate('/division-view')}
            className="text-xsm"
          >
            drill
          </Button>
        </span>
      ),
      dataIndex: 'percentage',
      key: 'percentage',
      width: 130,
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.percentage, b.percentage),
      render: (text: number) => (
        <span className="custom-table--column">
          {text.toFixed(0)} {'%'}
        </span>
      ),
    },
    {
      title: 'Total Revenue Supported  Annually',
      dataIndex: 'supportedRevenue',
      key: 'supportedRevenue',
      width: 150,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.supportedRevenue, b.supportedRevenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (isDivision) {
      navigate('/division-view');
    }
  }, [isDivision]);

  return (
    <div>
      <TitleUpdater title={`Corporate Dependency Matrix`} />
      <TitleBar
        title="Dependency Matrix"
        brief="This matrix shows the percentage that each of your locations supports across the business
                      at various levels of granularity."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title={'Corporate View'}
          columns={columns}
          data={filteredLocations}
          loading={loading}
          customStyles="custom-column-PA--heading custom-table--total-revenue"
          exportExcelColumns={baseColumnsCorporate}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
    </div>
  );
}

export default CorporateView;
